import React, { FunctionComponent, useState } from "react";

import {
  Grid,
  Typography,
  TextField,
  Fab,
  Button,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";

import { useConfigStyles } from "../../configStyles";
import { useStore } from "../../../../store";
import { SnackBar } from "../../../SnackBar";
import { getDecryptedData } from "../../../../utils/crypto";

interface PosConfigProps {
  editFlag: boolean;
  editData: any;
  goBackToList: () => void;
}
export const PosConfiguration: FunctionComponent<PosConfigProps> = ({
  editFlag,
  editData,
  goBackToList,
}) => {
  const classes = useConfigStyles();

  const [
    posConfigurations,
    { getPosConfigurations, createPosConfiguration, updatePosConfiguration },
  ] = useStore("posConfigurations");

  const [data, setData] = useState<Array<object>>([
    {
      id: 0,
      name: "",
      config_value: "",
      lbl_naming: "",
      display_order: "",
    },
  ]);
  const [snackbarColor, setSnackbarColor] = useState<string>("#d50000");
  const [posConfigStatus, setPosConfigStatus] = useState<string>("");
  const [submitFlag, setSubmitFlag] = useState<boolean>(false);

  const initialValues = {
    name: "",
    config_value: "",
    lbl_naming: "",
    display_order: "",
    is_sync: false,
  };

  if (editFlag === true) {
    initialValues.name = editData.name;
    initialValues.config_value =
      editData.name.slice(-3).toLowerCase() === "pwd"
        ? getDecryptedData(editData.config_value)
        : editData.config_value;
    initialValues.lbl_naming = editData.lbl_naming;
    initialValues.display_order = editData.display_order;
    initialValues.is_sync = editData.is_sync;
  }

  const posConfigSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    config_value: yup.string().required("Config Value is required"),
    lbl_naming: yup.string(),
    display_order: yup.string().matches(/(^[+]?[0-9]*$)/, {
      message: "Only numbers are allowed",
    }),
  });

  const addRow = (values: any, id: number) => {
    const available =
      posConfigurations &&
      posConfigurations.find(
        (i: any) => i.name.toLowerCase() === values.name.toLowerCase()
      );

    const filteredData = data && data.filter((i: any) => i.id !== id);
    if (
      available === undefined &&
      filteredData.find(
        (i: any) => i.name.toLowerCase() === values.name.toLowerCase()
      ) === undefined
    ) {
      const newData = {
        id: id + 1,
        name: "",
        config_value: "",
        lbl_naming: "",
        display_order: "",
        is_sync: false,
      };
      const dataArray = [...data, newData];
      setData(dataArray);
    } else {
      setSnackbarColor("red");
      setPosConfigStatus("Name already exists");
    }
  };

  const deleteRow = (id: number) => {
    const filteredArray = data.filter((i: any) => i.id !== id);
    setData(filteredArray);
  };

  const validateAll = (dataArray: any, values: any) => {
    const filteredStoreData =
      posConfigurations &&
      posConfigurations.filter((i: any) => i.name !== values.name);

    const names =
      editFlag === true
        ? filteredStoreData &&
          filteredStoreData.map((i: any) => i.name.toLowerCase())
        : posConfigurations &&
          posConfigurations.map((i: any) => i.name.toLowerCase());

    const dataNames =
      dataArray && dataArray.map((i: any) => i.name.toLowerCase());

    const availableData = dataNames.map((i: any) => {
      if (names.includes(i)) {
        return true;
      } else {
        return false;
      }
    });
    const dataFlag =
      editFlag === true
        ? isNaN(values.display_order)
          ? [true]
          : [false]
        : dataArray.map((i: any) => {
            if (isNaN(i.display_order)) {
              return true;
            } else {
              return false;
            }
          });

    const isNameExist =
      editFlag === true
        ? values.name === ""
          ? values.name
          : undefined
        : dataArray.find((i: any) => i.name === "");

    const isConfigValueExist =
      editFlag === true
        ? values.config_value === ""
          ? values.config_value
          : undefined
        : dataArray.find((i: any) => i.config_value === "");

    if (availableData.includes(true)) {
      setSnackbarColor("red");
      setPosConfigStatus("Name already exists");
    } else if (isNameExist !== undefined) {
      setSnackbarColor("red");
      setPosConfigStatus("Name is required");
    } else if (isConfigValueExist !== undefined) {
      setSnackbarColor("red");
      setPosConfigStatus("Config Value is required");
    } else if (dataFlag.includes(true)) {
      setSnackbarColor("red");
      setPosConfigStatus("Display Order should be a number");
    } else {
      submitAll(dataArray, values);
    }
  };

  const handleSubmit = (id: number, values: any) => {
    const filteredData = data.map((i: any) => {
      if (i.display_order === "") {
        i.display_order = null;
        return i;
      } else {
        return i;
      }
    });
    values.display_order =
      values.display_order === "" ? null : values.display_order;
    validateAll(filteredData, values);
  };

  const submitAll = async (data: any, values: any) => {
    try {
      const filteredArray = data.filter((i: any) => delete i.id);
      if (editFlag === true) {
        await updatePosConfiguration(editData.config_id, values);
        setSnackbarColor("green");
        setPosConfigStatus("Pos Configuration successfully updated");
        setTimeout(() => {
          getPosConfigurations();
          goBackToList();
        }, 2000);
      } else {
        await createPosConfiguration(filteredArray);
        setSnackbarColor("green");
        setPosConfigStatus("Pos Configuration successfully created");
        setTimeout(() => {
          getPosConfigurations();
          goBackToList();
        }, 2000);
      }
      setSubmitFlag(false);
      setData([
        {
          id: 0,
          name: "",
          config_value: "",
          lbl_naming: "",
          display_order: "",
          is_sync: false,
        },
      ]);
    } catch (error) {
      console.log(error);
      setSnackbarColor("red");
      setPosConfigStatus(error);
    }
  };

  const checkAvailability = (name: string, id: string) => {
    const available =
      posConfigurations &&
      posConfigurations.find(
        (i: any) => i.name.toLowerCase() === name.toLowerCase()
      );

    const filteredData = data && data.filter((i: any) => i.id !== id);
    if (
      available !== undefined ||
      filteredData.find(
        (i: any) => i.name.toLowerCase() === name.toLowerCase()
      ) !== undefined
    ) {
      setSnackbarColor("red");
      setPosConfigStatus("Name already exists");
    }
  };

  const onChangeHandle = (newName: string, id: number, type: string) => {
    if (type === "name") {
      data.map((i: any) => {
        if (i.id === id) {
          i.name = newName;
          return i;
        }
      });
    } else if (type === "config_value") {
      data.map((i: any) => {
        if (i.id === id) {
          i.config_value = newName;
          return i;
        }
      });
    } else if (type === "lbl_naming") {
      data.map((i: any) => {
        if (i.id === id) {
          i.lbl_naming = newName;
          return i;
        }
      });
    } else if (type === "display_order") {
      data.map((i: any) => {
        if (i.id === id) {
          i.display_order = newName;
          return i;
        }
      });
    } else if (type === "is_sync") {
      data.map((i: any) => {
        if (i.id === id) {
          i.is_sync = newName ? true : false;
          return i;
        }
      });
    }
  };

  const onCloseSnackbar = () => {
    setPosConfigStatus("");
  };

  return (
    <div>
      <SnackBar
        status={posConfigStatus}
        onCloseError={onCloseSnackbar}
        color={snackbarColor}
      />
      <Grid
        container
        spacing={2}
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Grid item xs={12}>
          <Typography className={classes.configTitle}>
            POS Configuration
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {data.map((i: any) => (
            <Grid container className={classes.mainGridStyle}>
              <Grid item>
                <Formik
                  enableReinitialize
                  validationSchema={posConfigSchema}
                  initialValues={initialValues}
                  onSubmit={(values) => {
                    addRow(values, i.id);
                  }}
                >
                  {({ values, handleChange, setFieldValue, resetForm }) => (
                    <Form>
                      <Grid container direction="row">
                        <Grid item>
                          <Grid
                            container
                            spacing={5}
                            className={classes.fieldSpace}
                            justify="center"
                            alignItems="center"
                          >
                            <Grid item xs className={classes.textFieldStyles}>
                              {i === data[0] ? (
                                <Typography
                                  className={classes.fieldLabelStyles}
                                >
                                  Name
                                </Typography>
                              ) : null}
                              <TextField
                                variant="outlined"
                                name="name"
                                value={editFlag === true ? values.name : i.name}
                                onChange={(e) => {
                                  handleChange(e);
                                  onChangeHandle(e.target.value, i.id, "name");
                                  checkAvailability(e.target.value, i.id);
                                }}
                                fullWidth={true}
                                disabled={editFlag}
                              />

                              <ErrorMessage name="name">
                                {(msg) => (
                                  <div style={{ color: "red" }}>{msg}</div>
                                )}
                              </ErrorMessage>
                            </Grid>
                            <Grid item xs className={classes.textFieldStyles}>
                              {i === data[0] ? (
                                <Typography
                                  className={classes.fieldLabelStyles}
                                >
                                  Config Value
                                </Typography>
                              ) : null}

                              <TextField
                                variant="outlined"
                                name="config_value"
                                value={
                                  editFlag === true
                                    ? values.config_value
                                    : i.config_value
                                }
                                type={
                                  values.name.slice(-3).toLowerCase() === "pwd"
                                    ? "password"
                                    : "text"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  onChangeHandle(
                                    e.target.value,
                                    i.id,
                                    "config_value"
                                  );
                                }}
                                fullWidth={true}
                              />

                              <ErrorMessage name="config_value">
                                {(msg) => (
                                  <div style={{ color: "red" }}>{msg}</div>
                                )}
                              </ErrorMessage>
                            </Grid>
                            <Grid item xs className={classes.textFieldStyles}>
                              {i === data[0] ? (
                                <Typography
                                  className={classes.fieldLabelStyles}
                                >
                                  Naming
                                </Typography>
                              ) : null}

                              <TextField
                                variant="outlined"
                                name="lbl_naming"
                                value={
                                  editFlag === true
                                    ? values.lbl_naming
                                    : i.lbl_naming
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  onChangeHandle(
                                    e.target.value,
                                    i.id,
                                    "lbl_naming"
                                  );
                                }}
                                fullWidth={true}
                              />

                              <ErrorMessage name="lbl_naming">
                                {(msg) => (
                                  <div style={{ color: "red" }}>{msg}</div>
                                )}
                              </ErrorMessage>
                            </Grid>
                            <Grid item xs className={classes.textFieldStyles}>
                              {i === data[0] ? (
                                <Typography
                                  className={classes.fieldLabelStyles}
                                >
                                  Display Order
                                </Typography>
                              ) : null}

                              <TextField
                                variant="outlined"
                                name="display_order"
                                value={
                                  editFlag === true
                                    ? values.display_order
                                    : i.display_order
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  onChangeHandle(
                                    e.target.value,
                                    i.id,
                                    "display_order"
                                  );
                                }}
                                fullWidth={true}
                              />

                              <ErrorMessage name="display_order">
                                {(msg) => (
                                  <div style={{ color: "red" }}>{msg}</div>
                                )}
                              </ErrorMessage>
                            </Grid>
                            <Grid item xs className={classes.checkbox}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.is_sync}
                                    onChange={(e) => {
                                      handleChange(e);
                                      onChangeHandle(
                                        e.target.checked.toString(),
                                        i.id,
                                        "is_sync"
                                      );
                                    }}
                                    name="is_sync"
                                    color="primary"
                                    style={{
                                      transform: "scale(1.5)",
                                    }}
                                  />
                                }
                                label={
                                  <Typography style={{ fontWeight: "bold" }}>
                                    Sync to all POS
                                  </Typography>
                                }
                              />
                            </Grid>
                            <Grid item>
                              <Grid
                                container
                                direction="row"
                                style={{ marginTop: i === 0 ? "1em" : "-1em" }}
                              >
                                {editFlag === false ? (
                                  <Grid
                                    item
                                    className={classes.textFieldStyles}
                                  >
                                    <Tooltip title="Add">
                                      <Fab
                                        size="small"
                                        aria-label="add"
                                        color="primary"
                                        type="submit"
                                        style={{
                                          fontSize: "14px",
                                          marginTop: "2em",
                                        }}
                                        disabled={
                                          submitFlag === true ||
                                          data.length === 5
                                        }
                                      >
                                        <AddCircleOutlineIcon />
                                      </Fab>
                                    </Tooltip>
                                  </Grid>
                                ) : null}
                                {i.id !== 0 && editFlag === false ? (
                                  <Grid
                                    item
                                    className={classes.textFieldStyles}
                                  >
                                    <Tooltip title="Delete">
                                      <Fab
                                        size="small"
                                        aria-label="delete"
                                        color="primary"
                                        disabled={submitFlag === true}
                                        style={{
                                          fontSize: "14px",
                                          marginTop: "2em",
                                        }}
                                        onClick={() => deleteRow(i.id)}
                                      >
                                        <DeleteIcon />
                                      </Fab>
                                    </Tooltip>
                                  </Grid>
                                ) : null}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>

                        {i === data[data.length - 1] ? (
                          <Grid item xs={12} className={classes.buttonsStyles}>
                            <Grid
                              container
                              direction="row"
                              justify="flex-end"
                              spacing={10}
                            >
                              <Grid item xs={12}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="medium"
                                  onClick={() => handleSubmit(i, values)}
                                >
                                  Submit
                                </Button>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  type="reset"
                                  size="medium"
                                  style={{
                                    marginLeft: "1em",
                                  }}
                                  onClick={() => {
                                    setData([
                                      {
                                        id: 0,
                                        name: "",
                                        config_value: "",
                                        lbl_naming: "",
                                        display_order: "",
                                      },
                                    ]);
                                    setSubmitFlag(false);
                                    resetForm();
                                  }}
                                >
                                  Reset
                                </Button>
                              </Grid>
                            </Grid>
                          </Grid>
                        ) : null}
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Grid>
            </Grid>
          ))}
          {data.length === 5 ? (
            <Grid item xs={12} className={classes.buttonsStyles}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  <label style={{ color: "red" }}>
                    Maximum 5 rows are allowed
                  </label>
                </Grid>
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </div>
  );
};
